import Button, { ButtonType } from './Button';
import { useContext, useState } from 'react';
import textData from "../textData.json";
import { LangContext, LangContextType } from "../utilities/customHooks";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "./ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";

type ActionButton = {
  buttonText: string,
  buttonCallback: () => void,
  buttonType?: ButtonType,
  buttonDisabled?: boolean,
}


function Modal(
  {
    isOpen,
    closeModal,
    modalHeader,
    modalHTML,
    contentLabel,
    primaryButton,
    secondaryButton,
    style: _style,
    nextDisabled,
    onNextClickCallback,
    prevDisabled,
    onPrevClickCallback,
    fullWidth
  }
  :
  {
    isOpen: boolean,
    closeModal: () => void,
    modalHeader?: string,
    modalHTML: JSX.Element | JSX.Element[],
    contentLabel: string,
    primaryButton?: ActionButton,
    secondaryButton?: ActionButton,
    /** @deprecated Ignored — Radix Dialog handles positioning via CSS */
    style?: unknown,
    nextDisabled?: boolean,
    onNextClickCallback?: () => void,
    prevDisabled?: boolean,
    onPrevClickCallback?: () => void,
    fullWidth?: boolean
  }
) {
  const { langState } = useContext(LangContext) as LangContextType
  const isMultiplePages = Array.isArray(modalHTML);
  const [pageIndex, setPageIndex] = useState(0);

  const handleCloseModal = () => {
    closeModal();
    setPageIndex(0);
  }

  const handleNextClick = () => {
    setPageIndex(i => i + 1);
    if (onNextClickCallback) onNextClickCallback();
  }

  const handlePrevClick = () => {
    setPageIndex(i => i - 1);
    if (onPrevClickCallback) onPrevClickCallback();
  }

  const fullWidthClass = fullWidth ? "w-full" : ""
  // gap-3 so a primary + secondary pair (e.g. "Go to preferences" / "Not now")
  // never render flush against each other.
  const primaryButtonClass = primaryButton ? "relative flex justify-center gap-3 h-[36px]" : "relative flex justify-center gap-3"

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleCloseModal(); }}>
      <DialogContent
        className={fullWidth ? "w-[90vw] max-w-[90vw]" : undefined}
        aria-describedby={undefined}
      >
        {/* Accessible title — visible or hidden */}
        {modalHeader ? (
          <DialogTitle className="h2 modalHeader font-semibold mb-4 text-center">
            {modalHeader}
          </DialogTitle>
        ) : (
          <VisuallyHidden.Root>
            <DialogTitle>{contentLabel}</DialogTitle>
          </VisuallyHidden.Root>
        )}

        {/* main modal content */}
        <div className={`flex flex-col ${fullWidthClass}`}>
          { isMultiplePages ? modalHTML[pageIndex] : modalHTML }
        </div>

        {/* button controls */}
        <div className={primaryButtonClass}>
          {/* PREV button */}
          { isMultiplePages && pageIndex > 0 &&
            <div className='absolute left-0 bottom-1/2 translate-y-1/2'>
              <Button
                buttonType='tertiary'
                buttonText={textData.Components.Modal.Prev[langState]}
                actionHandler={handlePrevClick}
                disabled={prevDisabled}
              />
            </div>
          }
          {primaryButton && <Button buttonType='primaryFull' buttonText={primaryButton.buttonText} actionHandler={() => primaryButton.buttonCallback()} disabled={primaryButton.buttonDisabled} /> }

          {/* secondary button */}
          { secondaryButton && <Button buttonType='secondary' buttonText={secondaryButton.buttonText} actionHandler={() => secondaryButton.buttonCallback()} disabled={secondaryButton.buttonDisabled} /> }

          {/* NEXT button */}
          { isMultiplePages && pageIndex < modalHTML.length - 1 &&
            <div className='absolute right-0 bottom-1/2 translate-y-1/2'>
              <Button
                buttonType='primary'
                buttonText={textData.Components.Modal.Next[langState]}
                actionHandler={handleNextClick}
                disabled={nextDisabled}
              />
            </div>
          }
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default Modal;
